import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Hls from 'hls.js';
import { Rnd } from 'react-rnd';
import CloseIcon from '@material-ui/icons/Close';
import HeightIcon from '@material-ui/icons/Height';
import LaunchIcon from '@material-ui/icons/Launch';
import SwitchFeed from '@material-ui/icons/SwitchVideo';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import trackingService from 'services/UITrackingService';
import PeekStore from 'stores/PeekStore';
import UserStore from 'stores/UserStore';
import UserFriendsFavoritesStore from 'stores/UserFriendsFavoritesStore';
import routeDefinitions from 'enum/routeDefinitions';
import { NOT_AVAILABLE } from 'utils/consts';

import styles from './PeekStream.css';


const peekWidth = 304;
const INTERVAL = 10000;

const breakPoint = 1280;
const chatWidth = {
  large: 494,
  small: 414,
};

@observer
class PeekStream extends Component {
  static propTypes = {
    index: PropTypes.number.isRequired,
    info: PropTypes.object.isRequired,
    changeEventStream: PropTypes.func,
    isBroadcasterChat: PropTypes.bool,
    isEventPeek: PropTypes.bool,
  };

  static defaultProps = {
    isBroadcasterChat: false,
    isEventPeek: false,
    changeEventStream: undefined,
  };

  constructor(...args) {
    super(...args);

    this.hls = new Hls();
    this.videoWrapRef = React.createRef();
    this.friendsFavoritesPresenceInterval = null;
    this.state = {
      isEventButtonDisabled: false,
    };
  }

  componentDidMount() {
    const { userType } = PeekStore;
    this.showStream();
    if (userType) {
      this.friendsFavoritesPresenceInterval = setInterval(this.getFFPresence, INTERVAL);
    }
  }

  componentDidUpdate(prevProps) {
    const { info: { cdnURL }, isEventPeek } = this.props;
    const isSourceChanged = cdnURL !== prevProps.info.cdnURL;
    if (isEventPeek && isSourceChanged) {
      if (this.hls) {
        this.hls.stopLoad();
        this.hls.detachMedia();
      }
      this.showStream();
    }
  }

  componentWillUnmount() {
    this.destroyStream();
    clearInterval(this.friendsFavoritesPresenceInterval);
  }

  showStream = () => {
    const { info: { canUseCDN, cdnURL }, isEventPeek } = this.props;
    const source = canUseCDN && cdnURL ? cdnURL : NOT_AVAILABLE;
    const notAvailable = source === NOT_AVAILABLE;
    if (isEventPeek) {
      this.setState({ isEventButtonDisabled: true });
    }
    if (Hls.isSupported() && !notAvailable) {
      const video = this.videoWrapRef.current.querySelectorAll('video')[0];
      this.hls.loadSource(source);
      this.hls.attachMedia(video);
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              if (isEventPeek) {
                this.setState({ isEventButtonDisabled: false });
              }
              video.volume = 0;
            })
            .catch(() => {
              this.destroyStream();
              video.src = '';
              if (isEventPeek) {
                this.setState({ isEventButtonDisabled: false });
              }
            });
        }
      });
    }
  };

  destroyStream = () => {
    if (this.hls) {
      this.hls.stopLoad();
      this.hls.detachMedia();
      this.hls.destroy();
    }
  };

  getFFPresence = () => {
    const { getOnlineFriendsOrFavourites } = UserFriendsFavoritesStore;
    const { userType } = PeekStore;
    getOnlineFriendsOrFavourites(userType);
  };

  getTrackingInformation = () => {
    const {
      info: { performerName },
      index,
    } = this.props;

    return { broadcaster: performerName, index };
  };

  trackPeekWindowEvent = (type, moment, event) => {
    trackingService.emit('interactWithPeekWindow', {
      action: type,
      info: this.getTrackingInformation(),
      ...moment && { moment },
      ...event && { event },
    });
  };

  onPeekWindowDrag = (event) => {
    this.trackPeekWindowEvent('move', 'during', event);
  };

  onPeekWindowDragStart = (event) => {
    this.trackPeekWindowEvent('move', 'start', event);
  };

  onPeekWindowDragStop = (event) => {
    this.trackPeekWindowEvent('move', 'end', event);
  };

  onPeekWindowResize = (event) => {
    this.trackPeekWindowEvent('resize', 'during', event);
  };

  onPeekWindowResizeStart = (event) => {
    this.trackPeekWindowEvent('resize', 'start', event);
  };

  onPeekWindowResizeStop = (event) => {
    this.trackPeekWindowEvent('resize', 'end', event);
  };

  onPeekWindowClick = () => {
    this.trackPeekWindowEvent('click');
  };

  onPeekWindowProfileLink = () => {
    this.trackPeekWindowEvent('goto');
  };

  onPeekWindowClose = () => {
    const { closePeekWindow } = PeekStore;
    const { broadcaster } = this.getTrackingInformation();

    closePeekWindow(broadcaster);

    this.trackPeekWindowEvent('close');
  };

  onRightClick = (e) => {
    e.preventDefault();
  };

  renderTitle = () => {
    const { isEventPeek, info: { performerName } } = this.props;
    if (isEventPeek) {
      return (
        <div className={styles.title}>
          {performerName}
        </div>
      );
    }

    return (
      <a
        className={styles.title}
        href={routeDefinitions.get(routeDefinitions.profile, { user: performerName })}
        rel="noopener noreferrer"
        target="_blank"
        onClick={this.onPeekWindowProfileLink}
      >
        {performerName}

        <LaunchIcon className={styles.goToIcon} />
      </a>
    );
  };

  renderCloseButton = () => {
    const { isEventPeek } = this.props;
    if (isEventPeek) {
      return null;
    }

    return (
      <button
        className={styles.closeButton}
        onClick={this.onPeekWindowClose}
      >
        <CloseIcon className={styles.closeIcon} />;
      </button>
    );
  };

  renderEventSwitch = () => {
    const { isEventPeek, changeEventStream } = this.props;
    const { isEventButtonDisabled } = this.state;

    if (!isEventPeek) {
      return null;
    }

    return (
      <button
        className={styles.switchFeedButton}
        disabled={isEventButtonDisabled}
        onClick={changeEventStream}
      >
        {translate('v2profile.view.as.main')}

        <SwitchFeed className={styles.switchIcon} />
      </button>
    );
  };

  renderResizeIcon = () => (
    <div className={styles.resizeContainer}>
      <HeightIcon className={styles.resizeIcon} />
    </div>
  );

  render() {
    const {
      info: { canUseCDN, cdnURL },
      index,
      isBroadcasterChat,
      isEventPeek,
    } = this.props;

    const source = canUseCDN ? cdnURL : NOT_AVAILABLE;
    const { user: { sfw } } = UserStore;
    const notAvailable = source === NOT_AVAILABLE;
    const videoClass = classNames({
      [styles.video]: true,
      [styles.sfwMode]: sfw,
    });

    let x = index * peekWidth;
    if (isEventPeek) {
      const windowWidth = window.innerWidth;
      const chatSize = windowWidth < breakPoint ? chatWidth.small : chatWidth.large;

      x = window.innerWidth - peekWidth - chatSize;
    }

    return (
      <Rnd
        bounds={isBroadcasterChat ? '#app' : '.pageContent'}
        className={styles.rndContainer}
        default={{
          width: `${peekWidth}px`,
          height: '172px',
          x,
          y: 0,
        }}
        dragHandleClassName={styles.content}
        enableResizing={{ bottomRight: true }}
        lockAspectRatio={true}
        minWidth="218px"
        resizeHandleComponent={{ bottomRight: this.renderResizeIcon() }}
        onClick={this.onPeekWindowClick}
        onDrag={this.onPeekWindowDrag}
        onDragStart={this.onPeekWindowDragStart}
        onDragStop={this.onPeekWindowDragStop}
        onResize={this.onPeekWindowResize}
        onResizeStart={this.onPeekWindowResizeStart}
        onResizeStop={this.onPeekWindowResizeStop}
      >
        <div ref={this.videoWrapRef} className={styles.peekWrapper}>
          <div className={styles.content}>
            {notAvailable ? (
              <div className={styles.notAvailable}>
                {translate('strings.Preview.notAvailable')}
              </div>
            ) : (
              <video
                className={videoClass}
                controlsList="nodownload"
                disablePictureInPicture={true}
                loop={true}
                muted={true}
                onContextMenu={this.onRightClick}
              />
            )}
          </div>

          {this.renderTitle()}
          {this.renderCloseButton()}
          {this.renderEventSwitch()}
        </div>
      </Rnd>
    );
  }
}

export default PeekStream;
